import axios from "axios";

import { generateRequestConfig, projectApiUrl } from "./constants";

export type StorageObjectClass = "avatar" | "general" | "document" | "kyc_document";

export type UploadedAttachment = {
  fileId: string;
  fileName: string;
  mimeType: string;
  sizeBytes?: number;
  url?: string;
  uploadedAt?: string;
};

/** Same-page signal: the shared navigation can show the new local preview while scanning finishes. */
export const PD_AVATAR_UPDATED_EVENT = "idexpress:pd-avatar-updated";

export function announceAvatarUpdated(previewUrl: string): void {
  window.dispatchEvent(new CustomEvent(PD_AVATAR_UPDATED_EVENT, { detail: { previewUrl } }));
}

/** An older avatar may be a URL string; a stored attachment object is never an img src. */
export const avatarPreviewFromStoredValue = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value : null;

/** A signed URL is short-lived, so resolve it on every view after scanning. */
export async function getAvatarDownloadUrl(): Promise<string | null> {
  const { data, status } = await axios.get(`${projectApiUrl}/personal_data/avatar-url`, {
    headers: { "X-Requested-With": "XMLHttpRequest" },
    withCredentials: true,
  });
  if (status === 202) return null;
  if (typeof data?.downloadUrl !== "string" || !data.downloadUrl) {
    throw new Error("Avatar download URL is missing");
  }
  return data.downloadUrl;
}

/**
 * The 3-step upload flow against api-storage.id.express, proxied through
 * api-pd.id.express: ticket -> PUT bytes straight to the same environment's
 * presigned Ceph RGW
 * URL (never through our own backend) -> complete. See
 * api-pd.id.express/backend/app/api/personal_data_attachments.py and
 * api-storage.id.express/docs/architecture.md for the full contract.
 */
export async function uploadAttachment(
  file: File,
  objectClass: StorageObjectClass,
  fieldPath: string
): Promise<UploadedAttachment> {
  const ticketConfig = generateRequestConfig("post", "personal_data_attachments", {
    fieldPath,
    objectClass,
    originalFilename: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  });
  const { data: ticket } = await axios(ticketConfig);

  // Do not use the shared axios instance here: it carries global AJAX/CSRF
  // headers intended for PD, and must not send those to an S3 endpoint.
  const uploadResponse = await fetch(ticket.uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
    credentials: "omit",
  });
  if (!uploadResponse.ok) {
    throw new Error(`S3 upload failed: ${uploadResponse.status}`);
  }

  const { data: completed } = await axios.post(
    `${projectApiUrl}/personal_data/attachments/${ticket.object.id}/complete`,
    { fieldPath },
    { headers: { "X-Requested-With": "XMLHttpRequest" } }
  );

  return completed.attachment;
}
